/**
 * 콜랩 상세(3.0 화면) 리포지토리 — 서버 사이드 fetch(게스트 접근).
 * @description `GET /web/v3/collabs/{id}` 와 그 아래 목록. 웹은 조회만 하고 참여는 앱으로 보낸다
 *   (서버 `WebCollabV3Controller`). 서버가 부르므로 API 주소가 번들에 실리지 않고 ISR 로 캐시된다.
 *   메타와 본문이 같은 주소를 부르면 Next 가 한 번으로 합친다.
 *
 *   목 모드(`NEXT_PUBLIC_API_MOCK=1`)는 어느 id 든 같은 목 콜랩을 돌려준다(`mocks/fixtures/collabV3`).
 */
import { toCollabV3Detail } from "./collabV3Mapper";
import { toCollabV3Thread } from "./collabV3ThreadMapper";
import { toCollabV3WorkPreviews } from "./collabV3WorkMapper";
import type {
  CollabV3DetailDto,
  CollabV3ThreadDto,
  CollabV3WorksDto,
} from "../dtos";
import type {
  CollabV3Detail,
  CollabV3Thread,
  CollabV3WorkPreview,
} from "../entities";
import {
  MOCK_COLLAB_V3_THREAD,
  MOCK_COLLAB_V3_WORKS,
  mockCollabV3Detail,
} from "@/mocks/fixtures/collabV3";
import { fetchJson } from "@/shared/lib/http";

const BASE_URL = process.env.API_BASE_URL;
const USE_MOCK = process.env.NEXT_PUBLIC_API_MOCK === "1";

/**
 * 콜랩 상세 — 2.0 · 3.0 모두 이 하나로 그린다(앱 `/collabs/[id]` 와 같다).
 * @description 없거나 볼 수 없는 콜랩은 4xx, 서버 장애는 5xx 로 던진다(`ApiError`). 가르는 것은
 *   부르는 쪽이다.
 */
export async function getCollabV3Detail(
  collabId: number,
): Promise<CollabV3Detail> {
  const context = `getCollabV3(${collabId})`;
  const dto = USE_MOCK
    ? mockCollabV3Detail(collabId)
    : await fetchJson<CollabV3DetailDto>(
        `${BASE_URL}/web/v3/collabs/${collabId}`,
        context,
      );
  return toCollabV3Detail(dto);
}

/**
 * 스레드 맛보기 — 첫 쪽(고정 글 + 최신순)에서 앞 `limit` 개. 쪽을 넘기지 않는다.
 * @description 고정 글은 `limit` 와 상관없이 첫 쪽에 다 오므로 받은 뒤에도 한 번 더 자른다.
 */
export async function getCollabV3Thread(
  collabId: number,
  limit: number,
): Promise<CollabV3Thread> {
  const dto = USE_MOCK
    ? MOCK_COLLAB_V3_THREAD
    : await fetchJson<CollabV3ThreadDto>(
        `${BASE_URL}/web/v3/collabs/${collabId}/thread?limit=${limit}`,
        `listCollabV3Thread(${collabId})`,
      );
  return toCollabV3Thread(dto, collabId, limit);
}

/** 작품 카드에 올릴 최신 작품 `limit` 개. 쪽을 넘기지 않는다 */
export async function getCollabV3WorkPreviews(
  collabId: number,
  limit: number,
): Promise<CollabV3WorkPreview[]> {
  const dto = USE_MOCK
    ? MOCK_COLLAB_V3_WORKS
    : await fetchJson<CollabV3WorksDto>(
        `${BASE_URL}/web/v3/collabs/${collabId}/works?limit=${limit}`,
        `listCollabV3Works(${collabId})`,
      );
  return toCollabV3WorkPreviews(dto, collabId).slice(0, limit);
}
